import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CtBinaryOperator, CtButtonConfiguration, CtControlConfiguration, CtControlService, CtControlTypes, CtControlValidator, CTFieldInfos, CtFormConfiguration, CTGeneralService, CtModelConfiguration, CTModelDatatableFilter, CtModelDatatableOperators, CtSelectControlOptions, CtSelectControlValue, CtSortOrderDescriptorParameter, CtThemeTypes, MAT_RAISED_PRIMARY, MAT_RAISED_WARN, SnackbarService } from '@ctsolution/ct-framework';
import { CTMGeneralService } from '../../../_core/lib/general.service';
import { FormBuilder, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef
} from "@angular/material/legacy-dialog";
import { WorkOrderPaymentRouteData } from '../../../_core/route-data/work-order-payment.route-data';
import { environment } from 'projects/ct-manager/src/environments/environment';
import { PaymentsEditConfiguration } from './payments-edit.configuration';
import { OriginPaymentEnum, WorkOrderPayment } from '../../../_core/classes/work-order-payments';
import { CustomCtFormContainerHelper } from '../../../_core/services/custom-ct-form.helper';
import { CTConfigurationModel } from '../../../_core/classes/ct-configuration-model';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { DataRequest, MethodEnum } from '@ctsolution/ct-webapi';

@Component({
  selector: 'app-payments-edit',
  templateUrl: './payments-edit.component.html',
  styleUrls: ['./payments-edit.component.scss']
})
export class PaymentsEditComponent {

  formConfiguration: CtFormConfiguration | null = null;

  private AmountControl = this.ctControlService.getDefaultConfiguration({
    name: 'Amount',
    // Label gestita via i18n (chiave `PaymentAmountLabel`): default IT = "Importo (€)",
    // override per-client tramite custom-translations (es. Waddi → "Importo").
    label: 'PaymentAmountLabel',
    type: CtControlTypes.NUMBER,
    required: false
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(33.33)

  private DescriptionControl = this.ctControlService.getDefaultConfiguration({
    name: 'Description',
    // Label gestita via i18n (chiave `PaymentDescriptionLabel`): default IT = "Informazioni",
    // override per-client tramite custom-translations (es. Waddi → "Causale").
    label: 'PaymentDescriptionLabel',
    // TEXTAREA: il campo è multiriga (la datatable mostra il valore con `\r\n`
    // tramite cellTemplate `white-space: pre-wrap`); coerente averlo come textarea
    // anche in fase di inserimento/modifica.
    type: CtControlTypes.TEXTAREA,
    required: false
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(100)

  private NotesControl = this.ctControlService.getDefaultConfiguration({
    name: 'Notes',
    // Label gestita via i18n (chiave `PaymentNotesLabel`): default IT = "Note",
    // override per-client tramite custom-translations (es. Waddi → "Condizioni di pagamento").
    label: 'PaymentNotesLabel',
    type: CtControlTypes.TEXTAREA
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(100);

  private PaymentDateControl = this.ctControlService.getDefaultConfiguration({
    name: 'PaymentDate',
    // Label gestita via i18n (chiave `PaymentDateLabel`): default IT = "Data fatturazione".
    label: 'PaymentDateLabel',
    type: CtControlTypes.DATE,
    required: false
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(33.33)
    .setNullValueEnabled(true);

  /**
   * Data di scadenza pagamento. Distinta da `PaymentDate` (= data di fatturazione).
   * Mappa sul campo `ExpireDate` della DTO `WorkOrderPayment`.
   * Reso `required` per-client tramite `paymentsRequiredFormControls`
   * (es. Waddi richiede l'obbligatorietà ai fini del calcolo scadenza).
   */
  private ExpireDateControl = this.ctControlService.getDefaultConfiguration({
    name: 'ExpireDate',
    // Label gestita via i18n (chiave `PaymentExpireDateLabel`): default IT = "Data scadenza pagamento".
    label: 'PaymentExpireDateLabel',
    type: CtControlTypes.DATE,
    required: false
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(33.33)
    .setNullValueEnabled(true);

  private CompanyControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'Company',
    controller: 'Company',
    // LOOKUP (non ENUMERABLE): renderizza un <input matInput> con <mat-autocomplete>
    // che invia la stringa digitata alla closure `setLookupFilter(value)`, abilitando
    // la ricerca incrementale. Con ENUMERABLE il framework usa un <mat-select> puro
    // che precarica tutte le opzioni e ignora la digitazione (il parametro `value`
    // resta sempre vuoto), rendendo inutilizzabile la lookup su dataset grandi.
    type: CtControlTypes.LOOKUP,
    required: false
  })
    // Label gestita via i18n (chiave `PaymentCompanyLabel`): default IT = "Azienda",
    // override per-client tramite custom-translations (es. Waddi → "Cliente").
    .setLabel("PaymentCompanyLabel")
    .setFlexSpacePercentage(33.33)
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        ?.setNullValueEnabled(true)

      options
        ?.setLookupFilter((value) => {

          // Filtro `IsSelf` configurabile per-client (vedi `paymentCompanyLookupSelfOnly`
          // in CTConfigurationModel):
          //  - null/true: applica IsSelf = true (comportamento storico)
          //  - false: nessun filtro su IsSelf, il lookup mostra tutte le aziende
          // La closure viene eseguita runtime al momento della query, quindi legge il
          // valore aggiornato di paymentConfiguration anche se questa è stata
          // caricata da `getComponentOverride` dopo l'inizializzazione del control.
          const filters: CTModelDatatableFilter[] = [];

          const selfOnly = this.paymentConfiguration.paymentCompanyLookupSelfOnly;
          if (selfOnly === null || selfOnly === true) {
            filters.push(
              CTModelDatatableFilter
                .create()
                .setField('IsSelf')
                .setValue(true)
                .setOperatorType(CtBinaryOperator.Equal)
            );
          }

          // Ricerca incrementale sul campo ReferenceName: per Company il campo
          // "ragione sociale visibile" è ReferenceName (vedi anche il setLabel del
          // setupForm che usa `model.Company.ReferenceName`). Filtrare su `Name`
          // restituirebbe match parziali su un campo diverso da quello mostrato
          // nell'input dell'autocomplete, confondendo l'utente.
          // Senza questo blocco, sovrascrivendo gli `operators` con `setLookupFilter`
          // annulleremmo la searchKey automatica applicata dal framework via
          // `setRemoteDefaults`, e il dropdown restituirebbe sempre l'intero dataset
          // paginato.
          if (value) {
            filters.push(
              CTModelDatatableFilter
                .create()
                .setField("[ReferenceName] is not null AND Contains([ReferenceName], ?)")
                .setValue([value])
                .setOperatorType(CtBinaryOperator.AndOperator)
            );
          }

          filters.push(this.customCtFormContainerHelper.getFilterForLookupCompanyOrContact(this.CompanyControl))

          const operators = CtModelDatatableOperators
            .create()
            .setFilters(filters);

          operators
            .Pagination
            ?.setSortOrders(new Array<CtSortOrderDescriptorParameter>(CtSortOrderDescriptorParameter.create('ReferenceName')))

          return operators;


        })


    });


  /**
   * Lookup del WorkOrder. Replicato dal pattern usato in `TaskEditComponent`:
   * tipo LOOKUP con endpoint remoto `POST WorkOrder/Lookup`, ricerca incrementale
   * gestita dal framework via searchKey, `hideSpinner` per UX. Facoltativo.
   *
   * Visibile SOLO se il control viene incluso nell'override
   * `paymentConfiguration.paymentsFormControls` (es. `['Company','WorkOrder','Amount',...]`).
   * Default `null` → il control non compare e non viene neanche aggiunto al FormGroup.
   *
   * Nel caso "dialog dal tab Fatturazione" il control viene comunque rimosso dal
   * form (vedi applyFormControlsConfiguration), perché il workorder è già implicito
   * dal `parameter.WorkOrderOid`.
   */
  private WorkOrderControl = this.ctControlService.getDefaultConfiguration({
    name: 'WorkOrder',
    type: CtControlTypes.LOOKUP,
    required: false,
    remoteValueOptionsEndpoint: DataRequest.create()
      .setController(['WorkOrder'])
      .setAction('Lookup')
      .setMethod(MethodEnum.POST)
      .setQueryParams({ hideSpinner: true })
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(33.33)
    // Label gestita via i18n (chiave `PaymentWorkOrderLabel`): default IT = "Rif. offerta",
    // override per-client tramite custom-translations se serve.
    .setLabel('PaymentWorkOrderLabel')
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        ?.setNullValueEnabled(true)
        .setLookupFilter((value) => {

          // Ricerca incrementale sul campo Name del WorkOrder (es. "WO-2026-001" o
          // descrizione commessa). Stesso pattern di ContactControl / CompanyControl,
          // ma su un campo diverso: per WorkOrder è `Name` la proprietà visibile.
          const filters: CTModelDatatableFilter[] = [];

          if (value) {
            filters.push(
              CTModelDatatableFilter
                .create()
                .setField("[Name] is not null AND Contains([Name], ?)")
                .setValue([value])
                .setOperatorType(CtBinaryOperator.AndOperator)
            );
          }

          // Filtro dinamico per Company: se l'utente ha già selezionato un Cliente,
          // restringiamo la lookup ai soli WorkOrder di quel cliente. Evita
          // combinazioni inconsistenti che genererebbero errori a submit lato BE.
          // La closure viene eseguita runtime a ogni query della lookup, quindi legge
          // il valore corrente di Company dal form (CtSelectControlValue → .value è l'Oid).
          const companyValue = this.formConfiguration?.form?.get('Company')?.value;
          const selectedCompanyOid = companyValue?.value ?? null;
          if (selectedCompanyOid) {
            filters.push(
              CTModelDatatableFilter
                .create()
                .setField('Company.Oid')
                .setValue(selectedCompanyOid)
                .setOperatorType(CtBinaryOperator.Equal)
            );
          }

          const operators = CtModelDatatableOperators
            .create()
            .setFilters(filters);

          operators
            .Pagination
            ?.setSortOrders(new Array<CtSortOrderDescriptorParameter>(CtSortOrderDescriptorParameter.create('Name')))

          return operators;

        })

    });


  private UserInfoFieldInfo: CTFieldInfos = CTFieldInfos.create({
    IsDTO: true,
    Field: "UserInfo",
    Type: "UserInfoDTO",
    IsNullable: false,
    ControlType: "Lookup",
    LookupInfos: {
      RemoteEndpoint: `${environment.dev.REST_API_SERVER}/User/LookupForUserAgents`,
      CodeField: "Code",
      DescriptionField: "Description",
      IncrementalSearch: true,
    },
  } as CTFieldInfos);

  private UserInfoControl: CtControlConfiguration = CtControlConfiguration
    .create(
      this.customCtFormContainerHelper
        .getCTFormControlByCTFieldInfo(this.UserInfoFieldInfo))
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(50)
    .setLabel('InternalReference')
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        .setNullValueEnabled(true)
        .setLookupFilter((value) => {

          const filters: CTModelDatatableFilter[] = [];

          if (value) {

            filters
              .push(
                CTModelDatatableFilter
                  .create()
                  .setField("NameSurname")
                  .setValue(value)
                  .setOperatorType(CtBinaryOperator.Contains)
              );
          }

          const operators = CtModelDatatableOperators
            .create()
            .setFilters(filters);

          operators
            .Pagination
            ?.setSortOrders(new Array<CtSortOrderDescriptorParameter>(CtSortOrderDescriptorParameter.create('Name')))

          return operators;

        })

    });

  private StatusControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'Status',
    controller: 'WorkOrderPaymentStatus',
    type: CtControlTypes.ENUMERABLE,
    required: false
  })
    .setTheme(CtThemeTypes.MATERIAL)
    .setFlexSpacePercentage(33.33)
    // Label gestita via i18n (chiave `PaymentStatusLabel`): default IT = "Stato".
    // I valori selezionabili (IN ATTESA / APPROVATO / EMESSO per Waddi) sono
    // popolati lato backend dal controller `WorkOrderPaymentStatus`, niente da
    // configurare client-side.
    .setLabel('PaymentStatusLabel')
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        ?.setNullValueEnabled(false)

    });

  private ContactControl = this.generalService
    .getLookupControlDefaultConfiguration({
      name: 'Contact',
      controller: 'Contact',
      key: 'Name',
      action: 'Lookup',
      required: false,
    })
    .setLabel('Amministratore')
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        .setNullValueEnabled(true)
        .setLookupFilter((value) => {

          const filters: CTModelDatatableFilter[] = [];

          if (value) {

            filters
              .push(
                CTModelDatatableFilter
                  .create()
                  .setField("[Name] is not null AND Contains([Name], ?)")
                  .setValue([value])
                  .setOperatorType(CtBinaryOperator.AndOperator)
              );
          }

        filters.push(this.customCtFormContainerHelper.getFilterForLookupCompanyOrContact(this.ContactControl))

          const operators = CtModelDatatableOperators
            .create()
            .setFilters(filters);

          operators
            .Pagination
            ?.setSortOrders(new Array<CtSortOrderDescriptorParameter>(CtSortOrderDescriptorParameter.create('Name')))

          return operators;

        })

    });


  cancelButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("CT_PAGE.CT_FORM.cancel")
    .setAction(() => this.close())
    .setMatherialOptions(MAT_RAISED_WARN);

  submitButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("CT_PAGE.CT_FORM.save")
    .setClass('ct-model-submit-button')
    .setAction(() => this.submit())
    .setMatherialOptions(MAT_RAISED_PRIMARY);


  workOrderOid: number | null = null;

  /**
   * Configurazione client-specific dei pagamenti. Risolta in `ngAfterViewInit`
   * via `getComponentOverride("payments", "paymentConfiguration", ...)`.
   * Quando `paymentsFormControls` è valorizzato, la lista dei controlli viene
   * filtrata mantenendo solo quelli elencati.
   */
  paymentConfiguration: CTConfigurationModel = CTConfigurationModel.create();

  /**
   * Espone al template se il componente è stato istanziato come dialog.
   * Stessa condizione già usata in `submit()`/`close()` per discriminare il caso
   * dialog dal caso route page. Serve al template per applicare la classe
   * `is-dialog` alla <mat-card> ed evitare il doppio bordo col
   * <mat-dialog-container>.
   */
  get isDialog(): boolean {
    return !!this.dialogRef?.close;
  }

  private controls = [
    this.DescriptionControl,
    this.AmountControl,
    this.PaymentDateControl,
    this.ExpireDateControl,
    this.StatusControl,
    this.CompanyControl,
    this.WorkOrderControl,
    this.UserInfoControl,
    this.ContactControl,
    this.NotesControl
  ]

  constructor(private cdr: ChangeDetectorRef,private customCtFormContainerHelper : CustomCtFormContainerHelper,private dialogRef: MatDialogRef<PaymentsEditComponent> | null, private route: ActivatedRoute, @Inject(MAT_DIALOG_DATA) public parameter: PaymentsEditConfiguration | null, private generalService: CTMGeneralService, private ctControlService: CtControlService, private formBuilder: FormBuilder, private Router: Router, private managerService: CtManagerService, private snackbar: SnackbarService) {

    // Costruzione iniziale del form con tutti i controlli. La risoluzione dell'eventuale
    // override (filtro per-client) avviene in ngAfterViewInit dopo getComponentOverride.
    this.formConfiguration = CtFormConfiguration
      .create(this.formBuilder.group({}))
      .setTheme(CtThemeTypes.MATERIAL)
      .setControls(this.controls);

  }

  async ngAfterViewInit() {

    // 1) Risolve l'override per-client (paymentsFormControls).
    await this.managerService
      .getComponentOverride("payments", "paymentConfiguration", this.paymentConfiguration);

    // 2) Applica gli adattamenti del form (filtro per override + rimozione Company nel caso work-order).
    this.applyFormControlsConfiguration();

    // 3) Sottoscrive il valueChanges di Company per resettare la lookup WorkOrder
    // e notificare l'utente. Deve girare DOPO applyFormControlsConfiguration (altrimenti
    // i FormControl potrebbero essere stati rimossi/rigenerati e la subscription
    // sarebbe su un handle stale). Vale solo per la pagina globale (nel caso
    // work-order Company e WorkOrder sono entrambi rimossi dal form).
    this.wireCompanyWorkOrderCoherence();

    // 4) Carica i dati del record (se siamo in edit).
    this.setup();

  }

  /**
   * Mantiene coerente la coppia Cliente / Rif. offerta: al cambio di Company reset
   * del WorkOrder già selezionato + snackbar informativa all'utente.
   *
   * IMPORTANTE: al cambio di Company dobbiamo forzare la lookup di WorkOrder a
   * rifare la query verso il BE, altrimenti il framework mantiene in cache i
   * risultati caricati all'init del form (quando Company non era ancora selezionato)
   * e mostrerà comunque tutti i workorder finché l'utente non digita.
   * Vedi ct-lookup-material-control.setupGetter: la pipe `valueChanges + startWith('')
   * + distinctUntilChanged + switchMap` triggera la query solo su emissioni distinte
   * del control. Emettendo prima `''` poi `null` provochiamo due emissioni distinte
   * garantite, forzando il refetch che rileggerà il filtro Company aggiornato dalla
   * nostra closure `setLookupFilter`.
   *
   * L'evento di reset iniziale (setupForm in edit) NON deve mostrare lo snackbar:
   * usiamo un flag `firstCompanyChange` per saltare la primissima emissione, che
   * corrisponde al patchValue di caricamento del record esistente.
   */
  private wireCompanyWorkOrderCoherence(): void {

    const form = this.formConfiguration?.form;
    if (!form) return;

    const companyCtrl = form.get('Company');
    const workOrderCtrl = form.get('WorkOrder');

    // Se uno dei due non è nel form corrente, non c'è nulla da orchestrare
    // (es. caso work-order: entrambi rimossi da applyFormControlsConfiguration).
    if (!companyCtrl || !workOrderCtrl) return;

    let firstCompanyChange = true;

    companyCtrl.valueChanges.subscribe(() => {

      // Salta la primissima emissione: corrisponde al setValue iniziale in edit
      // (setupForm), quando l'utente non ha ancora interagito.
      if (firstCompanyChange) {
        firstCompanyChange = false;
        return;
      }

      const hadWorkOrder = !!workOrderCtrl.value;

      // Reset del WorkOrder + snackbar solo se effettivamente valorizzato.
      if (hadWorkOrder) {
        this.WorkOrderControl.setValue(null);
        this.snackbar.open('PaymentWorkOrderResetOnCompanyChange');
      }

      // In ogni caso forza il re-fetch della lookup WorkOrder: due emissioni
      // distinte consecutive (`'' → null`) attraversano il `distinctUntilChanged`
      // interno di setupGetter e scatenano una nuova query verso il BE, che ora
      // includerà il filtro `Company.Oid` aggiornato tramite la closure setLookupFilter.
      // Nota: se hadWorkOrder era true, WorkOrderControl.setValue(null) qui sopra
      // ha già emesso una prima transizione (oggetto → null); l'emissione '' → null
      // di sotto è comunque distinta e ri-triggera la query, che è quello che vogliamo.
      workOrderCtrl.setValue('', { emitEvent: true });
      workOrderCtrl.setValue(null, { emitEvent: true });

    });

  }

  /**
   * Adatta la lista dei controlli del form in base a:
   *  - override per-client (`paymentConfiguration.paymentsFormControls`): se valorizzato,
   *    filtra e riordina i controlli mantenendo solo quelli elencati;
   *  - presenza di un `WorkOrderOid` proveniente dal chiamante (`parameter.WorkOrderOid`):
   *    in tal caso il campo `Company` viene rimosso dal form, perché il backend lo
   *    valorizzerà a partire dal work-order associato.
   */
  private applyFormControlsConfiguration(): void {

    let effectiveControls = this.controls;

    // (a) Filtro per-client.
    const allowed = this.paymentConfiguration.paymentsFormControls;
    if (allowed && allowed.length > 0) {

      // Mantengo l'ordine fornito in `allowed`, non quello originale.
      effectiveControls = allowed
        .map(name => this.controls.find(c => c.name === name))
        .filter((c): c is CtControlConfiguration => !!c);

    }

    // (b) Caso work-order: Company non va richiesto, sarà derivato da WorkOrderOid.
    if (this.parameter?.WorkOrderOid) {
      effectiveControls = effectiveControls.filter(c => c.name !== 'Company');

      // Anche il control `WorkOrder` (se eventualmente incluso nell'override) qui è
      // ridondante: l'OID del work-order è già implicito nel `parameter.WorkOrderOid`
      // passato dal chiamante (WorkOrderPaymentsComponent). Lo rimuoviamo per evitare
      // un campo "vuoto/disabled" inutile nel form.
      effectiveControls = effectiveControls.filter(c => c.name !== 'WorkOrder');

      // Miglioramento grafico: senza Company il controllo Amount resterebbe al 50%
      // lasciando metà riga vuota. Lo espandiamo a 100% per occupare tutta la larghezza,
      // coerente con gli altri controlli del form ridotto (Description, Notes).
      // L'operazione è sicura sui client esistenti perché il branch è raggiungibile
      // solo se il chiamante passa `WorkOrderOid` (caso introdotto dal tab Fatturazione).
      this.AmountControl.setFlexSpacePercentage(100);
    }

    // Riallinea sia il form group che la configuration.
    // Rimuoviamo dal form group i control non più inclusi (evita validatori "orfani"
    // su campi nascosti che bloccherebbero il submit).
    const form = this.formConfiguration?.form;
    if (form) {
      const keepNames = new Set(effectiveControls.map(c => c.name));
      Object.keys(form.controls)
        .filter(name => !keepNames.has(name))
        .forEach(name => form.removeControl(name));
    }

    this.formConfiguration?.setControls(effectiveControls);

    // (c) Applica eventuale override `paymentsRequiredFormControls`: forza i control
    // elencati a `required` runtime. Lavoriamo SUL form group (Validators.required)
    // perché il flag `required` sul CtControlConfiguration governa solo il rendering
    // dell'asterisco nella label: senza il validator sul FormControl, l'utente potrebbe
    // comunque submittare con il campo vuoto. Applichiamo solo ai control effettivamente
    // presenti nel form (intersezione con `effectiveControls`).
    const requiredNames = this.paymentConfiguration.paymentsRequiredFormControls;
    if (form && requiredNames && requiredNames.length > 0) {

      const effectiveNamesSet = new Set(effectiveControls.map(c => c.name));

      requiredNames
        .filter(name => effectiveNamesSet.has(name))
        .forEach(name => {

          // Marca il CtControlConfiguration come required (per il rendering dell'asterisco).
          const ctrlCfg = effectiveControls.find(c => c.name === name);
          ctrlCfg?.setRequired(true);

          // Aggiunge Validators.required sul FormControl reale (per la validazione).
          const ctrl = form.get(name);
          if (ctrl) {
            ctrl.setValidators(Validators.required);
            ctrl.updateValueAndValidity({ emitEvent: false });
          }

        });

    }

    this.cdr.detectChanges();

  }

  setup() {


    const pId = this.route.snapshot.paramMap.get("id");
    const id = pId ?? this.parameter?.Oid;

    if (id == null) return;

    this.generalService.getInfos(
      {
        controller: 'WorkOrderPayment',
        oid: +id
      }
    ).subscribe(result => {

      if (result.Result) {
        this.setupForm(result.Result.DataSource)
        if (result.Result.DataSource.WorkOrder?.Oid)
          this.workOrderOid = result.Result.DataSource.WorkOrder.Oid;

      }


    })

  }


  setupForm(model: any) {

    this.formConfiguration?.form?.patchValue(model);

    if (model.Status) {

      this.StatusControl.setValue(model.Status.Oid)

    }

    if (model.UserInfo) {

      this.UserInfoControl.setValue(CtSelectControlValue.create().setValue(model.UserInfo.Oid).setLabel(model.UserInfo.NameSurname))

    }

    if (model.Company) {

      // CompanyControl è di tipo LOOKUP: il value del FormControl è un
      // CtSelectControlValue ({label, value, disabled}), non l'Oid grezzo.
      // Usiamo `ReferenceName` come label visibile nell'input (stesso pattern
      // di UserInfo/Contact). Prima passavamo solo l'Oid e l'autocomplete
      // mostrava la label vuota.
      this.CompanyControl.setValue(
        CtSelectControlValue
          .create()
          .setValue(model.Company.Oid)
          .setLabel(model.Company.ReferenceName)
      )

    }

    if (model.WorkOrder) {

      // WorkOrderControl è di tipo LOOKUP: stesso pattern di Company/UserInfo/Contact.
      // Come label visibile usiamo `Name` (concorda con `task-edit` che fa la stessa
      // cosa: `setLabel(dataSource.WorkOrder?.Name ?? null)`). Se model.WorkOrder.Name
      // non è valorizzato l'input mostrerà vuoto ma il value resta corretto per il submit.
      this.WorkOrderControl.setValue(
        CtSelectControlValue
          .create()
          .setValue(model.WorkOrder.Oid)
          .setLabel(model.WorkOrder.Name ?? null)
      )

    }

    if (model.Contact) {

      this.ContactControl.setValue(CtSelectControlValue.create().setValue(model.Contact.Oid).setLabel(model.Contact.Name))

    }

    this.cdr.detectChanges();

  }



  async submit() {

    this.formConfiguration?.form?.markAllAsTouched();

    if (this.formConfiguration?.form?.valid) {

      const data: WorkOrderPayment = WorkOrderPayment.create(this.formConfiguration?.form.value)

      if (this.parameter?.WorkOrderOid || this.workOrderOid)
        data.setWorkOrderOid(this.parameter?.WorkOrderOid ? this.parameter?.WorkOrderOid : this.workOrderOid)
      else if (this.formConfiguration.form.get('WorkOrder')?.value)
        // Caso form globale: WorkOrder è stato scelto dall'utente nella lookup
        // (control facoltativo, abilitato via paymentsFormControls). Il value del
        // FormControl LOOKUP è un CtSelectControlValue: l'Oid sta in `.value.value`.
        // Questo ramo NON è raggiungibile nei casi tab work-order, dove il valore
        // arriva da `parameter.WorkOrderOid` (gestito sopra).
        data.setWorkOrderOid(this.formConfiguration.form.get('WorkOrder')?.value.value)

      if (this.formConfiguration.form.get('Status')?.value)
        data.setStatusOid(this.formConfiguration.form.get('Status')?.value)

      if (this.formConfiguration.form.get('Company')?.value)
        // .value.value: il CompanyControl è di tipo LOOKUP, quindi il valore del FormControl
        // è un CtSelectControlValue ({label, value, disabled}). L'Oid è nel campo `.value`
        // (stesso pattern di UserInfo e Contact qui sotto). Quando il control era
        // ENUMERABLE invece, il value era direttamente l'Oid — da cui il bug post-switch.
        data.setCompanyOid(this.formConfiguration.form.get('Company')?.value.value)
      else if (this.parameter?.CompanyOid)
        // Fallback per il caso work-order: `Company` è stato rimosso dal form da
        // applyFormControlsConfiguration (b) perché il pagamento eredita la company
        // dal work-order. Il caller (WorkOrderPaymentsComponent) ha valorizzato
        // `parameter.CompanyOid` leggendolo dal viewModel.workOrder.Company.Oid,
        // senza chiamate API aggiuntive. Per i client esistenti, dove Company resta
        // nel form, questo ramo non viene mai eseguito (zero impatto sul pregresso).
        data.setCompanyOid(this.parameter.CompanyOid)

      if (this.formConfiguration.form.get('UserInfo')?.value)
        data.setUserInfoOid(this.formConfiguration.form.get('UserInfo')?.value.value)

      if (this.formConfiguration.form.get('Contact')?.value)
        data.setContactOid(this.formConfiguration.form.get('Contact')?.value.value)

      data.setOrigin(OriginPaymentEnum.MANUALE)

      const model: CtModelConfiguration<any> = CtModelConfiguration.create().setRouteData(WorkOrderPaymentRouteData());

      const pId = this.route.snapshot.paramMap.get("id");
      const id = pId ?? this.parameter?.Oid;

      if (id)
        model.RouteData?.setId(+id)

      this.generalService.putData(model, data)?.then(() => {
        if (this.dialogRef?.close) {
          this.dialogRef?.close();
          window.location.reload();
        }
        else
          this.Router.navigate(['payments'])

      });

    }

  }

  close() {

    if (this.dialogRef?.close) {
      this.dialogRef?.close();
    }
    else
      this.Router.navigate(['payments'])

  }


}

export enum RecipientEnum {

  Amministrator = 1,
  Client = 2,
  IsSelf = 3

}
